import React, { useRef, useCallback, useEffect } from "react";
import ReactDatetime from "react-datetime";
import moment from "moment";

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_YEAR = new Date().getFullYear() + 30;
const MIN_YEAR = 1900;

/**
 * "MM-DD-YYYY" has display positions 0-9.
 * Map display pos → digit index (0-7), -1 = dash position.
 */
const DISP_TO_DIGIT = [0, 1, -1, 2, 3, -1, 4, 5, 6, 7];
/** Map digit index (0-7) → display position in "MM-DD-YYYY" */
const DIGIT_TO_DISP = [0, 1, 3, 4, 6, 7, 8, 9];

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** Format raw digit string (0–8 chars) → partial display string with dashes */
function digitsToDisplay(digits) {
    if (!digits.length) return '';
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return digits.slice(0, 2) + '-' + digits.slice(2);
    return digits.slice(0, 2) + '-' + digits.slice(2, 4) + '-' + digits.slice(4);
}

/**
 * Validate whether placing `digit` at `idx` (0-7) in the digit sequence is valid.
 * `digits8` is the current 8-char padded digit string (pre-edit).
 */
function isDigitValid(idx, digit, digits8) {
    const arr = (digits8 + '00000000').slice(0, 8).split('');
    arr[idx] = digit;

    if (idx === 0) return parseInt(digit, 10) <= 1;                // mm[0]: 0 or 1
    if (idx === 1) {
        const mm = parseInt(arr.slice(0, 2).join(''), 10);
        return mm >= 1 && mm <= 12;                                 // mm: 01-12
    }
    if (idx === 2) return parseInt(digit, 10) <= 3;                 // dd[0]: 0-3
    if (idx === 3) {
        const dd = parseInt(arr.slice(2, 4).join(''), 10);
        return dd >= 1 && dd <= 31;                                 // dd: 01-31
    }
    if (idx === 4) {
        const y0 = parseInt(digit, 10);
        return y0 >= 1 && y0 <= 2;                                  // yyyy[0]: 1 or 2
    }
    if (idx === 5) {
        const y2 = parseInt(arr.slice(4, 6).join(''), 10);
        return y2 === 19 || y2 === 20;                              // yyyy[0-1]: 19 or 20
    }
    if (idx === 6) return true;                                     // yyyy[2]: any
    if (idx === 7) {
        const year = parseInt(arr.slice(4, 8).join(''), 10);
        return year >= MIN_YEAR && year <= MAX_YEAR;                // full year range
    }
    return true;
}

/**
 * Auto-pad: returns true when a single digit typed at a 2-digit segment's
 * first position can never be the start of a valid 2-digit value, so we should
 * prepend '0' automatically.
 */
function shouldAutoPad(segFirstDigitIdx, digit) {
    if (segFirstDigitIdx === 0) return parseInt(digit, 10) >= 2;   // month ≥ 2 → pad '0'
    if (segFirstDigitIdx === 2) return parseInt(digit, 10) >= 4;   // day   ≥ 4 → pad '0'
    return false;
}

// ─── Component ────────────────────────────────────────────────────────────────
const CustomDatePicker = (props) => {
    const isDateOnly = props.timeFormat === false;

    /** Raw digit string (no dashes), 0-8 chars */
    const digitsRef = useRef('');
    /** DOM <input> element */
    const inputElRef = useRef(null);
    /**
     * Overtype cursor: digit index (0-7) when a complete date is typed and user
     * is editing by overwriting. `null` = normal append mode.
     */
    const overtypeCursorRef = useRef(null);

    // ── Init from external value ─────────────────────────────────────────────
    const initDone = useRef(false);
    if (!initDone.current && isDateOnly && props.value) {
        initDone.current = true;
        const m = moment.isMoment(props.value)
            ? props.value
            : moment(props.value, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        if (m.isValid()) {
            digitsRef.current = m.format('MM-DD-YYYY').replace(/\D/g, '');
            if (digitsRef.current.length === 8) overtypeCursorRef.current = 0;
        }
    }

    // ── Sync if parent change value ──────────────────────────────────────────
    useEffect(() => {
        if (!isDateOnly) return;
        if (!props.value || props.value === '') {
            digitsRef.current = '';
            overtypeCursorRef.current = null;
            if (inputElRef.current) inputElRef.current.value = '';
        } else {
            const m = moment.isMoment(props.value)
                ? props.value
                : moment(props.value, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
            if (m.isValid()) {
                const newDigits = m.format('MM-DD-YYYY').replace(/\D/g, '');
                if (newDigits !== digitsRef.current) {
                    digitsRef.current = newDigits;
                    if (inputElRef.current) inputElRef.current.value = digitsToDisplay(newDigits);
                    if (newDigits.length === 8) overtypeCursorRef.current = 0;
                }
            }
        }
    }, [props.value, isDateOnly]);

    // ── DOM write helpers ────────────────────────────────────────────────────
    /** Write digitsRef to DOM input, then optionally place cursor at a digit index */
    const flush = useCallback((targetDigitIdx) => {
        const el = inputElRef.current;
        if (!el) return;
        el.value = digitsToDisplay(digitsRef.current);
        if (targetDigitIdx !== undefined && targetDigitIdx !== null && digitsRef.current.length === 8) {
            const dispPos = DIGIT_TO_DISP[Math.min(targetDigitIdx, 7)];
            el.setSelectionRange(dispPos, dispPos);
        }
    }, []);

    const notifyComplete = useCallback(() => {
        if (digitsRef.current.length === 8) {
            const m = moment(digitsToDisplay(digitsRef.current), 'MM-DD-YYYY', true);
            if (m.isValid() && props.onChange) props.onChange(m);
        }
    }, [props.onChange]);

    // ── renderInput ──────────────────────────────────────────────────────────
    const renderInput = useCallback((inputProps, openCalendar, closeCalendar) => {

        // ── Date-Time passthrough ───────────────────────────────────────────
        if (!isDateOnly) {
            return (
                <input
                    {...inputProps}
                    maxLength={19}
                    onKeyDown={(e) => {
                        const nav = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                            'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Enter'];
                        if (!nav.includes(e.key) && !e.ctrlKey && !e.metaKey)
                            if (!/^[0-9\- :APMapm/]$/.test(e.key)) e.preventDefault();
                        if (inputProps.onKeyDown) inputProps.onKeyDown(e);
                    }}
                />
            );
        }

        // ── Date-only: fully self-managed ───────────────────────────────────

        const handlePaste = (e) => {
            e.preventDefault();
            const text = (e.clipboardData || window.clipboardData).getData('text');
            if (!text) return;

            const formats = [
                'MM/DD/YYYY', 'MM-DD-YYYY', 'M/D/YYYY', 'M-D-YYYY',
                'YYYY-MM-DD', 'YYYY/MM/DD', 'DD/MM/YYYY', 'DD-MM-YYYY',
                'MM/DD/YY', 'M/D/YY',
            ];
            let parsed = null;
            for (const fmt of formats) {
                const m = moment(text.trim(), fmt, true);
                if (m.isValid()) { parsed = m; break; }
            }
            if (!parsed) { const m = moment(text.trim()); if (m.isValid()) parsed = m; }
            if (!parsed || parsed.year() < MIN_YEAR || parsed.year() > MAX_YEAR) return;

            const formatted = parsed.format('MM-DD-YYYY');
            digitsRef.current = formatted.replace(/\D/g, '');
            overtypeCursorRef.current = 0;
            flush(0);
            if (props.onChange) props.onChange(parsed);
        };

        const handleClick = (e) => {
            // If date is complete, enter overtype mode at the clicked digit
            if (digitsRef.current.length === 8) {
                const dispPos = Math.min(e.target.selectionStart ?? 0, 9);
                let digitIdx = DISP_TO_DIGIT[dispPos];
                if (digitIdx === -1) {
                    // Clicked on a dash — move to next segment start
                    digitIdx = dispPos <= 2 ? 2 : 4;
                }
                overtypeCursorRef.current = digitIdx;
                // Place cursor at correct display pos
                const dp = DIGIT_TO_DISP[digitIdx];
                e.target.setSelectionRange(dp, dp);
                return;
            }
            // Partial or empty — let calendar open normally
            overtypeCursorRef.current = null;
            if (inputProps.onClick) inputProps.onClick(e);
        };

        const handleKeyDown = (e) => {
            const { key, ctrlKey, metaKey, altKey } = e;

            // Allow system shortcuts
            if (ctrlKey || metaKey || altKey) {
                if (inputProps.onKeyDown) inputProps.onKeyDown(e);
                return;
            }

            // Tab / Enter / Escape pass through
            if (['Tab', 'Enter', 'Escape'].includes(key)) {
                if (inputProps.onKeyDown) inputProps.onKeyDown(e);
                return;
            }

            e.preventDefault();

            // ── Arrow navigation (segment-level in overtype, char-level otherwise) ──
            if (key === 'ArrowLeft' || key === 'ArrowRight') {
                if (overtypeCursorRef.current !== null) {
                    const delta = key === 'ArrowLeft' ? -1 : 1;
                    overtypeCursorRef.current = Math.max(0, Math.min(7, overtypeCursorRef.current + delta));
                    flush(overtypeCursorRef.current);
                }
                return;
            }

            // ── Backspace / Delete ────────────────────────────────────────────
            if (key === 'Backspace' || key === 'Delete') {
                // Always: remove last digit and exit overtype mode
                overtypeCursorRef.current = null;
                if (!digitsRef.current.length) return;
                digitsRef.current = digitsRef.current.slice(0, -1);
                flush();
                if (!digitsRef.current.length && props.onChange) props.onChange('');
                return;
            }

            // ── Digit keys ────────────────────────────────────────────────────
            if (!/^\d$/.test(key)) return;

            // ── OVERTYPE MODE (date complete, cursor positioned) ───────────────
            if (overtypeCursorRef.current !== null) {
                const idx = overtypeCursorRef.current;
                const digits8 = digitsRef.current.padEnd(8, '0');

                // Auto-pad: e.g. typing '5' at mm[0] → insert '05' and jump to dd
                if (shouldAutoPad(idx, key)) {
                    if (!isDigitValid(idx, '0', digits8)) return;
                    if (!isDigitValid(idx + 1, key, ('0' + digits8.slice(1)))) return;
                    const arr = digits8.split('');
                    arr[idx] = '0';
                    arr[idx + 1] = key;
                    digitsRef.current = arr.join('');
                    const next = idx + 2;
                    overtypeCursorRef.current = Math.min(next, 7);
                    flush(overtypeCursorRef.current);
                    notifyComplete();
                    return;
                }

                if (!isDigitValid(idx, key, digits8)) return;

                const arr = digits8.split('');
                arr[idx] = key;
                digitsRef.current = arr.join('');

                // Advance cursor; skip already-at-7 case
                const next = Math.min(idx + 1, 7);
                overtypeCursorRef.current = next;
                flush(next);
                notifyComplete();
                return;
            }

            // ── APPEND MODE ───────────────────────────────────────────────────
            const digitPos = digitsRef.current.length;
            if (digitPos >= 8) {
                // Already full but not in overtype — start overtype
                overtypeCursorRef.current = 0;
                flush(0);
                return;
            }

            // Auto-pad for append mode
            if (shouldAutoPad(digitPos, key)) {
                const digits8 = digitsRef.current.padEnd(8, '0');
                if (!isDigitValid(digitPos, '0', digits8)) return;
                if (!isDigitValid(digitPos + 1, key, (digitsRef.current + '0').padEnd(8, '0'))) return;
                digitsRef.current += '0' + key;
                flush();
                if (digitsRef.current.length === 8) {
                    overtypeCursorRef.current = 7;
                    notifyComplete();
                }
                return;
            }

            if (!isDigitValid(digitPos, key, digitsRef.current.padEnd(8, '0'))) return;

            digitsRef.current += key;
            flush();
            if (digitsRef.current.length === 8) {
                overtypeCursorRef.current = 7;
                notifyComplete();
            }
        };

        return (
            <input
                className={inputProps.className || 'form-control'}
                placeholder={inputProps.placeholder || 'MM-DD-YYYY'}
                style={inputProps.style || { borderRadius: 0 }}
                disabled={inputProps.disabled}
                name={inputProps.name}
                id={inputProps.id}
                required={inputProps.required}
                maxLength={10}
                autoComplete="off"
                defaultValue={digitsToDisplay(digitsRef.current)}
                ref={(el) => {
                    inputElRef.current = el;
                    if (el) el.value = digitsToDisplay(digitsRef.current);
                }}
                onChange={() => { }}
                onKeyDown={handleKeyDown}
                onClick={handleClick}
                onPaste={handlePaste}
                onFocus={inputProps.onFocus}
                onBlur={inputProps.onBlur}
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally empty — all state lives in stable refs

    // ── ReactDatetime onChange (calendar pick) ───────────────────────────────
    const handleWrapperChange = (val) => {
        if (!isDateOnly) {
            if (typeof val === 'string') {
                const t = val.trim();
                if (t === '' && props.onChange) props.onChange('');
                else if (t.length >= 16 && props.onChange) props.onChange(val);
            } else if (props.onChange) {
                props.onChange(val);
            }
            return;
        }

        if (moment.isMoment(val) && val.isValid()) {
            const formatted = val.format('MM-DD-YYYY');
            digitsRef.current = formatted.replace(/\D/g, '');
            overtypeCursorRef.current = 0;
            if (inputElRef.current) inputElRef.current.value = formatted;
            if (props.onChange) props.onChange(val);
        }
    };

    const mergedInputProps = {
        className: props.inputProps?.className || 'form-control',
        placeholder: props.inputProps?.placeholder || (isDateOnly ? 'MM-DD-YYYY' : 'MM-DD-YYYY HH:mm'),
        style: props.inputProps?.style || { borderRadius: 0 },
        disabled: props.inputProps?.disabled,
        name: props.inputProps?.name,
        id: props.inputProps?.id,
        required: props.inputProps?.required,
    };

    return (
        <ReactDatetime
            strictParsing={true}
            renderInput={renderInput}
            {...props}
            onChange={handleWrapperChange}
            inputProps={mergedInputProps}
        />
    );
};

export default CustomDatePicker;
